import { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api.js';
import { Loading, Empty, Avatar, StatusPill, useToast, Modal, Field, PageHeader, Segmented } from '../../components/ui.jsx';
import Icon from '../../components/Icon.jsx';
import { relativeTime } from '../../lib/format.js';

const TABS = [
  ['open', 'Open'],
  ['resolved', 'Resolved'],
  ['dismissed', 'Dismissed'],
  ['', 'All'],
];

export default function Complaints() {
  const toast = useToast();
  const [tab, setTab] = useState('open');
  const [rows, setRows] = useState(null);
  const [acting, setActing] = useState(null); // { complaint, status }
  const [resolution, setResolution] = useState('');

  const load = useCallback(() => {
    setRows(null);
    api.get('/api/admin/complaints', { status: tab }).then(setRows).catch(() => setRows([]));
  }, [tab]);

  useEffect(load, [load]);

  const submit = async () => {
    try {
      await api.post(`/api/admin/complaints/${acting.complaint.id}`, {
        status: acting.status,
        resolution,
      });
      setRows((list) => list.filter((c) => c.id !== acting.complaint.id || tab === ''));
      setActing(null);
      setResolution('');
      toast.success(`Complaint ${acting.status}.`);
      if (tab === '') load();
    } catch (err) {
      toast.error(err);
    }
  };

  return (
    <>
      <PageHeader title="Complaints" subtitle="Review and close issues raised by patients and nurses." />

      <Segmented options={TABS} value={tab} onChange={setTab} className="mb-3" style={{ maxWidth: 480 }} />

      {!rows ? (
        <Loading rows={2} />
      ) : rows.length === 0 ? (
        <Empty art="success" title={tab === 'open' ? 'Everything is handled' : 'Nothing here'}>
          {tab === 'open' ? 'No open complaints right now.' : 'No records in this list.'}
        </Empty>
      ) : (
        <div className="stack">
          {rows.map((c, i) => (
            <article key={c.id} className="card anim-rise" style={{ '--i': i }}>
              <div className="card-header">
                <Avatar name={c.user_name} avatar={c.user_avatar} size="sm" />
                <div className="grow">
                  <h3>{c.subject}</h3>
                  <div className="tiny dim">
                    {c.user_name} ({c.user_role}) · {c.user_email} · {relativeTime(c.created_at)}
                    {c.booking_id ? ` · booking #${c.booking_id}` : ''}
                  </div>
                </div>
                <StatusPill status={c.status} />
              </div>
              <div className="card-body">
                <p className="muted small">{c.message}</p>
                {c.resolution && (
                  <div className="banner banner-success mt-2" style={{ marginBottom: 0 }}>
                    <Icon name="check-circle" />
                    <span className="grow">{c.resolution}</span>
                  </div>
                )}
              </div>
              {c.status === 'open' && (
                <div className="card-footer">
                  <button className="btn btn-success btn-sm" onClick={() => setActing({ complaint: c, status: 'resolved' })}>
                    <Icon name="check" /> Mark resolved
                  </button>
                  <button className="btn btn-sm" onClick={() => setActing({ complaint: c, status: 'dismissed' })}>
                    Dismiss
                  </button>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {acting && (
        <Modal
          title={acting.status === 'resolved' ? 'Resolve complaint' : 'Dismiss complaint'}
          onClose={() => setActing(null)}
          size={480}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setActing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={submit}>
                Confirm
              </button>
            </>
          }
        >
          <p className="small muted">“{acting.complaint.subject}”</p>
          <Field
            label="Response to the user"
            id="resolution"
            hint="Sent as a notification to whoever raised the complaint."
          >
            <textarea
              id="resolution"
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
              placeholder="We have spoken to the nurse and issued a partial refund."
            />
          </Field>
        </Modal>
      )}
    </>
  );
}
